import React from "react";
import {
  Box,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Stack,
  Divider,
  useTheme,
} from "@mui/material";
import ImageOrderingEditor, {
  ImageOrderingValue,
} from "../ImageOrderingEditor";
import TagMapEditor from "../TagMapEditor";
import AddTagMapButton from "../AddTagMapButton";
import { suggestionsForMap } from "../standardTags";
import SyndicatorCard from "../SyndicatorCard";

interface Props {
  config: any;
  onChange: (field: string, value: any) => void;
}

const DEFAULT_TAGMAPS = [
  "exterior_TagMap",
  "manual_TagMap",
  "featured_TagMap",
  "pano_TagMap",
];

export const crsDefaults = {
  syndicatorData: {
    stockNumbers: false,
    isPostRecon: false,
    auctionCode: "",
    aeSettings: {
      allowLocalReconCalculation: false,
      buttonHighlightCode: "",
      apiUrl: "",
      buttonHighlightImagePath: "",
      postRecon: false,
      encoding: "",
      aePano: false,
      auctionCode: "",
      panoCode: "",
    },
    tagMapping: {
      exterior_TagMap: {},
      manual_TagMap: {},
      featured_TagMap: {},
      pano_TagMap: {},
    },
  },
  imageOrdering: {
    ImageOrder: "",
  },
};

const CRSForm: React.FC<Props> = ({ config, onChange }) => {
  const theme = useTheme();
  const accent = theme.palette.status.accent;

  const syndicatorData = config.syndicatorData || {};
  const aeSettings = syndicatorData.aeSettings || {};
  const tagMapping = syndicatorData.tagMapping || {};
  const tagMapNames = Array.from(
    new Set([
      ...DEFAULT_TAGMAPS,
      ...Object.keys(tagMapping).filter((k) => k.endsWith("_TagMap")),
    ]),
  );
  const imageOrdering: ImageOrderingValue = config.imageOrdering || {};

  return (
    <SyndicatorCard title="CRS Configuration">
      <Stack spacing={3}>
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, color: accent, fontWeight: 600 }}
          >
            Syndicator Data
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Auction Code"
                fullWidth
                size="small"
                value={syndicatorData.auctionCode || ""}
                onChange={(e) =>
                  onChange("syndicatorData.auctionCode", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={syndicatorData.isPostRecon ?? false}
                      onChange={(e) =>
                        onChange(
                          "syndicatorData.isPostRecon",
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label={
                    <Typography sx={{ fontWeight: 500 }}>Post Recon</Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={syndicatorData.stockNumbers ?? false}
                      onChange={(e) =>
                        onChange(
                          "syndicatorData.stockNumbers",
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label={
                    <Typography sx={{ fontWeight: 500 }}>
                      Stock Numbers
                    </Typography>
                  }
                />
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, color: accent, fontWeight: 600 }}
          >
            AE Settings
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Auction Code"
                fullWidth
                size="small"
                value={aeSettings.auctionCode || ""}
                onChange={(e) =>
                  onChange(
                    "syndicatorData.aeSettings.auctionCode",
                    e.target.value,
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="API URL"
                fullWidth
                size="small"
                value={aeSettings.apiUrl || ""}
                onChange={(e) =>
                  onChange("syndicatorData.aeSettings.apiUrl", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Encoding"
                fullWidth
                size="small"
                value={aeSettings.encoding || ""}
                onChange={(e) =>
                  onChange(
                    "syndicatorData.aeSettings.encoding",
                    e.target.value,
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Pano Code"
                fullWidth
                size="small"
                value={aeSettings.panoCode || ""}
                onChange={(e) =>
                  onChange(
                    "syndicatorData.aeSettings.panoCode",
                    e.target.value,
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Button Highlight Code"
                fullWidth
                size="small"
                value={aeSettings.buttonHighlightCode || ""}
                onChange={(e) =>
                  onChange(
                    "syndicatorData.aeSettings.buttonHighlightCode",
                    e.target.value,
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Button Highlight Image Path"
                fullWidth
                size="small"
                value={aeSettings.buttonHighlightImagePath || ""}
                onChange={(e) =>
                  onChange(
                    "syndicatorData.aeSettings.buttonHighlightImagePath",
                    e.target.value,
                  )
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Switch
                      checked={aeSettings.aePano ?? false}
                      onChange={(e) =>
                        onChange(
                          "syndicatorData.aeSettings.aePano",
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label={
                    <Typography sx={{ fontWeight: 500 }}>AE Pano</Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={aeSettings.postRecon ?? false}
                      onChange={(e) =>
                        onChange(
                          "syndicatorData.aeSettings.postRecon",
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label={
                    <Typography sx={{ fontWeight: 500 }}>Post Recon</Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={aeSettings.allowLocalReconCalculation ?? false}
                      onChange={(e) =>
                        onChange(
                          "syndicatorData.aeSettings.allowLocalReconCalculation",
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label={
                    <Typography sx={{ fontWeight: 500 }}>
                      Allow Local Recon Calc
                    </Typography>
                  }
                />
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, color: accent, fontWeight: 600 }}
          >
            Tag Mapping
          </Typography>
          <Stack spacing={2}>
            {tagMapNames.map((mapName) => {
              const isDefault = DEFAULT_TAGMAPS.includes(mapName);
              return (
                <TagMapEditor
                  key={mapName}
                  label={mapName}
                  value={tagMapping[mapName]}
                  onChange={(next) =>
                    onChange(`syndicatorData.tagMapping.${mapName}`, next)
                  }
                  onRemove={
                    isDefault
                      ? undefined
                      : () => {
                          const next = { ...tagMapping };
                          delete next[mapName];
                          onChange("syndicatorData.tagMapping", next);
                        }
                  }
                  keyLabel="Tag"
                  valueLabel="CRS Label"
                  tagSuggestions={suggestionsForMap(mapName)}
                />
              );
            })}
            <AddTagMapButton
              existingNames={tagMapNames}
              onAdd={(name) =>
                onChange(`syndicatorData.tagMapping.${name}`, {})
              }
            />
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, color: accent, fontWeight: 600 }}
          >
            Image Ordering
          </Typography>
          <ImageOrderingEditor
            value={imageOrdering}
            onChange={(next) => onChange("imageOrdering", next)}
          />
        </Box>
      </Stack>
    </SyndicatorCard>
  );
};

export default CRSForm;
